// @flow

import _ from 'lodash';
import FreshRequest from 'fresh-request';
// app
import ko from 'rgs-knockout';
import type { Observable } from 'rgs-knockout';
import { interceptor } from 'app/v2/common/decorators/class';
import { EventEmitter } from 'event-emitter';
import { ObservableBusy } from 'observable-busy';
import { createJSableEntityListMapExtender, JSableOmitPrivateMixin } from 'jsable';
import type { ResponseData, ResponseWrapper } from 'app-my-dms-insurance/app/data/response';

// ko-component-adaptive-table
import type { ITableComponent } from './contract.flow';
import type { DataProviderParams } from './data-provider';
import { DataProvider } from './data-provider';
import { scrollable } from './features';
import type { PaginationModelParams } from './pagination';
import { PaginationModel, PaginationPageItemModel } from './pagination';
import type { TableSortingParams } from './sorting';
import { EVENT_SORTING_CHANGE, TableSortingModel } from './sorting';

export type AdaptiveTableRowModelParams = {
	entity: any,
	params: {
		options: Object,
		model: Object
	}
}

export type AdaptiveTableDataModelParams = {
	dataProvider: DataProviderParams,
	rowModel: any,
	enableScroll?: Observable<boolean>,
	pagination?: PaginationModelParams,
	busy?: ObservableBusy,
	sorting?: TableSortingParams,
	settings: {
		countRequestPerTime: number,
		withRequestCountControl: boolean,
	}
};

export class AdaptiveTableRowModel {
	entity: any;
	_options: Object;
	_model: Object;

	constructor (params: AdaptiveTableRowModelParams) {
		this.entity = params.entity;
		this._options = params.params.options;
		this._model = params.params.model;
	}
}

export class AdaptiveTableDataModel extends JSableOmitPrivateMixin(EventEmitter) {
	dataProvider: DataProvider;
	pagination: PaginationModel;
	outerBusy: ?ObservableBusy;
	busy: ObservableBusy;
	sorting: TableSortingModel;

	_params: AdaptiveTableDataModelParams;
	_subcomponents: ITableComponent[] = [];
	_freshFetch: FreshRequest;

	constructor (params: AdaptiveTableDataModelParams) {
		super();

		this._freshFetch = new FreshRequest();
		const rowModel: AdaptiveTableRowModel = params.rowModel;

		this._params = _.defaults(params, {
			enableScroll: false,
		});

		this.busy = new ObservableBusy();

		this.outerBusy = params.busy;

		this.Items = ko.observableArray([])
			.extend(createJSableEntityListMapExtender(
				rowModel.entity,
				[
					rowModel._options,
					rowModel._model,
				],
			));

		this.dataProvider = new DataProvider(params.dataProvider);

		this.pagination = new PaginationModel(params.pagination);

		this.addComponent(
			this._initSortingComponent(params.sorting),
		);

		this.on('__pagination_change_page', ({ value, disabled }): void => {
			if (!disabled && value
				&& value !== this.pagination._currentPage()
				&& !this.busy.isBusy()) {

				this.pagination._currentPage(value);

				this._updateData();
			}
		});

		if (params.settings && params.settings.withRequestCountControl) {
			this._updateData = _.debounce(
				this._updateData.bind(this), params.settings.countRequestPerTime,
			);
		}
	}

	get isScrollEnabled (): boolean {
		return ko.unwrap(this._params.enableScroll);
	}

	_initSortingComponent (sortingOptions: TableSortingParams): ITableComponent {
		const sorting = this.sorting = new TableSortingModel(sortingOptions);

		sorting.on(EVENT_SORTING_CHANGE, (): void => {
			const additionalUpdateParams = this.sorting.toJS();
			this._updateData(additionalUpdateParams);
		});

		return sorting;
	}

	addComponent (item: ITableComponent) {
		this._subcomponents.push(item);
	}

	async _fetchData (params: any = {}) {
		// TODO: терущая реализация работы через подмену ответа любого запроса на последний отправленный запрос
		//  такое поведение приводит к перезаписи данных в теблице при завершении каждого отправленного запроса
		//  в идеале очередь запросов должна завершаться единожды
		//  (при появлении нового запроса старый не должен возвращать свой результат)
		const response: ResponseWrapper<ResponseData<*>>
			= await this._freshFetch.fetch(this.dataProvider.getData(params));

		if ((params.from > 0) && (response.Data.Items.length === 0)) {
			// Если пришли не на первую страницу и не получили результат,
			// то жёстко переходим на перую страницу
			const paramsFirstPage = { ...params, from: 0 };
			const responseFirstPage: ResponseWrapper<ResponseData<*>>
				= await this._freshFetch.fetch(this.dataProvider.getData(paramsFirstPage));
			this.pagination._currentPage(1);
			this.fromJS(responseFirstPage.Data);

		} else {
			this.fromJS(response.Data);
		}
	}

	_aggregateInnerParams () {
		const paginationParams = this.pagination.toJS();
		const sortingParams = (this.sorting._params.enabled && this.sorting.toJS()) || {};

		return _.merge(paginationParams, sortingParams);
	}

	_updateData (additionalParams: any = {}): void {
		const innerParams = this._aggregateInnerParams();
		const requestParams = _.merge(innerParams, additionalParams);

		const promise = this._fetchData(requestParams);

		this.busy.add(promise);

		if (this.outerBusy) {
			this.busy.add(promise);
		}
	}

	fromJS (js: { Total: number }) {
		this.pagination.fromJS(js);

		return super.fromJS(js);
	}

	mapPaginationItemToView (item: PaginationPageItemModel) {
		const { disabled, active, additionalCssClass } = item || {};
		const result = {
			disabled: disabled || this.busy.isBusy(),
			active,
		};

		if (additionalCssClass) {
			result[additionalCssClass] = additionalCssClass;
		}

		return result;
	}

	dispose = (): void => {
		_.each(this._subcomponents, (item: ITableComponent) => item.dispose());
	};
}

export type AdaptiveTableViewModelParams = {
	model: AdaptiveTableDataModel;
}

@interceptor(scrollable)
export default class AdaptiveTableViewModel {
	_model: AdaptiveTableDataModel;
	rowTemplate: ?string;
	headerTemplate: ?string;
	emptyTemplate: ?string;
	waitingTemplate: ?string;

	constructor (params: AdaptiveTableViewModelParams, elementContent: any) {
		this._model = params.model;

		this.rowTemplate = this._getChildNodes(elementContent.templateNodes, 'row-template');
		this.headerTemplate = this._getChildNodes(elementContent.templateNodes, 'header-template');

		this.emptyTemplate = this._getChildNodes(elementContent.templateNodes, 'empty-template');
		this.waitingTemplate = this._getChildNodes(elementContent.templateNodes,
			'waiting-template',
		);
	}

	dispose = (): void => {
		this._model.dispose();
	};

	_getChildNodes (allNodes: any, tagName: string): Node[] {
		const lowerTagName = tagName.toLowerCase();

		const node = _.find(allNodes,
			node => node.tagName && node.tagName.toLowerCase() === lowerTagName,
		);

		const children = node ? node.childNodes : null;

		return children;
	}
}
